import { useConversations } from "@coaching-culture/api-client";
import {
  Button,
  Circle,
  DatePill,
  Flex,
  Panel,
  Table,
  Text,
  UserFlag,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { ScrollableContainer } from "components/ScrollableContainer";
import { Toggle } from "components/Toggle";
import { fromUnixTime } from "date-fns";
import { FaCommentDots, FaEye } from "react-icons/fa";
import { useLocalStorage } from "react-use";

export const RecentConversationsWidget = () => {
  const { data: convos } = useConversations();
  const [includeIncomplete, setIncludeIncomplete] = useLocalStorage(
    "convo-widget-incomp",
    false,
  );
  const user = useUser();

  const mappedConvos = (convos ?? []).flatMap((x) =>
    x.occurences
      .map((o) => ({
        ...x,
        ...o,
        cancelledOn: o.cancelledOn ?? x.cancelledOn,
      }))
      .filter(
        (x) => fromUnixTime(x.scheduledDate + x.duration * 60) < new Date(),
      )
      .filter((x) => includeIncomplete || x.completedOn != null),
  );

  const notMe = (convo: (typeof mappedConvos)[number]) =>
    convo.directReport.id === user[0].id ? convo.manager : convo.directReport;

  return (
    <Panel width={"100%"}>
      <Flex borderBottom={1} height={83} justifyContent={"space-between"} p={4}>
        <Flex alignItems="center">
          <Circle icon={FaCommentDots} size={"medium"} color="primary" mr={2} />
          <Text
            fontSize={4}
            fontWeight={600}
            lineHeight={1}
            mr={1}
            color="black"
            as="h1"
          >
            Recent Conversations
          </Text>
        </Flex>
        <Toggle
          leftLabel="Completed"
          rightLabel="All"
          value={includeIncomplete}
          onChange={setIncludeIncomplete}
        />
      </Flex>

      <ScrollableContainer maxHeight={310}>
        <Table>
          <thead
            style={{
              top: 0,
              background: "white",
              position: "sticky",
              marginBottom: "20px",
              zIndex: 3,
            }}
          >
            <tr>
              <th>With Whom</th>
              <th>Scheduled On</th>
              <th>Completed On</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {mappedConvos.length === 0 ? (
              <tr>
                <td colSpan={4}>
                  <Text textAlign="center" fontWeight={600} color="grey2">
                    None to show
                  </Text>
                </td>
              </tr>
            ) : null}
            {mappedConvos
              .sort((a, b) => b.scheduledDate - a.scheduledDate)
              .map((x) => (
                <tr>
                  <td>
                    <UserFlag user={notMe(x)} />
                  </td>
                  <td>
                    <DatePill value={x.scheduledDate} />
                  </td>
                  <td>
                    <DatePill
                      value={x.completedOn}
                      color={x.completedOn == null ? undefined : "positive"}
                    />
                  </td>
                  <td>
                    <Button
                      color="primary"
                      icon={FaEye}
                      to={`/solutions/performance/conversations/${x.id}/${x.index}/play`}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </ScrollableContainer>
    </Panel>
  );
};
